import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { auth, db } from "../firebase";
import Header from "../components/Header";
import "../index.css";

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 좋아요 상태
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isPinned, setIsPinned] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const likeBusyRef = useRef(false);

  useEffect(() => {
    // Auth Listener
    const unsubscribeAuth = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        // 관리자 권한 확인 (Firestore에서)
        try {
          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists() && userSnap.data().isAdmin === true) {
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
          }
        } catch (error) {
          console.error("Error checking admin status:", error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    });

    // Fetch Post
    const fetchPost = async () => {
      try {
        const docRef = doc(db, "posts", id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPost({ id: docSnap.id, ...data });
          
          // 좋아요 초기 상태 설정
          const likedBy = data.likedBy || [];
          setLikesCount(likedBy.length);
          if (auth.currentUser && likedBy.includes(auth.currentUser.uid)) {
            setIsLiked(true);
          }
          // 고정 상태 설정
          setIsPinned(data.isPinned || false);
        } else {
          alert("존재하지 않는 게시글입니다.");
          navigate("/");
        }
      } catch (error) {
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();

    // Subscribe to Comments
    const q = query(collection(db, "posts", id, "comments"), orderBy("createdAt", "asc"));
    const unsubscribeComments = onSnapshot(q, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setComments(commentsData);
    });

    return () => {
      unsubscribeAuth();
      unsubscribeComments();
    };
  }, [id, navigate]);

  // user가 로드된 후 좋아요 상태 재확인
  useEffect(() => {
    if (user && post && post.likedBy) {
      setIsLiked(post.likedBy.includes(user.uid));
    }
  }, [user, post]);

  const handleLike = async () => {
    if (!user) {
      alert("로그인이 필요합니다.");
      return;
    }
    if (likeBusyRef.current) return;
    likeBusyRef.current = true;
    setLikeBusy(true);
    const postRef = doc(db, "posts", id);
    try {
      if (isLiked) {
        await updateDoc(postRef, { likedBy: arrayRemove(user.uid) });
        setIsLiked(false);
      } else {
        await updateDoc(postRef, { likedBy: arrayUnion(user.uid) });
        setIsLiked(true);
      }
      const snap = await getDoc(postRef);
      const data = snap.data() || {};
      const likedBy = data.likedBy || [];
      setLikesCount(likedBy.length);
    } catch (error) {
      console.error("Error toggling like:", error);
    } finally {
      setLikeBusy(false);
      likeBusyRef.current = false;
    }
  };

  const handlePin = async () => {
    if (!isAdmin) {
      alert("관리자 권한이 필요합니다.");
      return;
    }

    const postRef = doc(db, "posts", id);
    try {
      await updateDoc(postRef, { isPinned: !isPinned });
      setIsPinned(!isPinned);
      alert(isPinned ? "게시글 고정이 해제되었습니다." : "게시글이 상단에 고정되었습니다.");
    } catch (error) {
      console.error("Error toggling pin:", error);
      alert("오류가 발생했습니다: " + error.message);
    }
  };

  const handleCommentSubmit = async () => {
    if (!newComment.trim()) return;
    if (!user) {
      alert("로그인이 필요합니다.");
      return;
    }

    try {
      // 사용자 프로필 사진(Base64) 가져오기
      let authorPhoto = user.photoURL;
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists() && userDoc.data().photoBase64) {
          authorPhoto = userDoc.data().photoBase64;
        }
      } catch (err) {
        console.error("Failed to fetch user profile photo:", err);
      }

      await addDoc(collection(db, "posts", id, "comments"), {
        text: newComment,
        author: user.displayName || "Anonymous",
        authorId: user.uid,
        authorPhoto: authorPhoto,
        createdAt: serverTimestamp(),
      });
      setNewComment("");
    } catch (error) {
      console.error("Error adding comment:", error);
      alert("댓글 작성 실패");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
  };

  if (loading) return <div style={{ textAlign: "center", marginTop: "50px" }}>로딩 중...</div>;
  if (!post) return null;

  return (
    <>
      <Header />
      <main className="post-detail-container">
        <button className="btn-back" onClick={() => navigate(-1)}>← 뒤로가기</button>

        <h1 className="detail-title">{post.title}</h1>

        <div className="detail-meta">
          <div className="author-info">
             <img src={post.authorPhoto || "https://via.placeholder.com/36"} alt="author" className="author-avatar-small" />
             <span>by <b>{post.author}</b></span>
          </div>
          <span>·</span>
          <span>{formatDate(post.createdAt)}</span>
        </div>

        <div className="detail-content" style={{ minHeight: "200px", whiteSpace: "pre-wrap" }}>
          {post.content}
        </div>

        <div className="post-actions" style={{ marginTop: "48px", borderTop: "1px solid #eee", paddingTop: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button 
            className={`action-item ${isLiked ? "liked" : ""}`} 
            onClick={handleLike}
            disabled={likeBusy}
            style={{ fontSize: "1.2rem", color: isLiked ? "red" : "inherit", background: "none", border: "none", cursor: likeBusy ? "not-allowed" : "pointer", opacity: likeBusy ? 0.6 : 1, pointerEvents: likeBusy ? "none" : "auto" }}
          >
            {isLiked ? "❤️" : "🤍"} 좋아요 {likesCount}
          </button>

          {isAdmin && (
            <button 
              onClick={handlePin}
              style={{ 
                padding: "8px 16px", 
                borderRadius: "20px", 
                border: isPinned ? "1px solid #e64980" : "1px solid #adb5bd", 
                backgroundColor: isPinned ? "#fff0f6" : "white",
                color: isPinned ? "#e64980" : "#495057",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "0.9rem"
              }}
            >
              {isPinned ? "📌 고정 해제" : "📌 상단 고정"}
            </button>
          )}
        </div>

        {/* 댓글 섹션 */}
        <div className="comments-section" style={{ marginTop: "48px" }}>
          <h3 style={{ fontSize: "1.2rem", fontWeight: "bold", marginBottom: "16px" }}>댓글 {comments.length}개</h3>
          
          <div className="comment-form" style={{ display: "flex", gap: "12px", marginBottom: "32px" }}>
            <input 
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="댓글을 작성하세요..."
              style={{ flex: 1, padding: "12px", borderRadius: "4px", border: "1px solid #ddd" }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCommentSubmit();
              }}
            />
            <button 
              className="btn-primary" 
              onClick={handleCommentSubmit}
              style={{ padding: "0 24px" }}
            >
              등록
            </button>
          </div>

          <div className="comments-list" style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {comments.map(comment => (
              <div key={comment.id} className="comment-item" style={{ display: "flex", gap: "12px" }}>
                <img 
                  src={comment.authorPhoto || "https://via.placeholder.com/36"} 
                  alt="commenter" 
                  style={{ width: "36px", height: "36px", borderRadius: "50%", objectFit: "cover" }} 
                />
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                    <span style={{ fontWeight: "bold" }}>{comment.author}</span>
                    <span style={{ fontSize: "0.8rem", color: "#868e96" }}>{formatDate(comment.createdAt)}</span>
                  </div>
                  <p style={{ margin: 0, color: "#333", whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "break-word" }}>{comment.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}
